import { ResourceStock } from '../systems/ResourceManager';

// === DÉFINITIONS DES BÂTIMENTS ===
// Chaque bâtiment a 3 niveaux (certains commencent "non construits" = niveau 0).
// Les upgrades coûtent des ressources + 1 run de temps.

export type BuildingId = 'dormitory' | 'storage' | 'workshop' | 'watchtower' | 'altar' | 'tavern';

/** Effet d'un niveau de bâtiment */
export interface BuildingLevelConfig {
  label: string;           // description courte du niveau
  cost?: Partial<ResourceStock>; // coût pour atteindre ce niveau (absent = niveau de base)
}

/** Définition complète d'un bâtiment */
export interface BuildingDefinition {
  id: BuildingId;
  name: string;
  emoji: string;
  description: string;
  /** Niveau minimum (0 = non construit, 1 = de base) */
  minLevel: number;
  /** Niveaux disponibles (index = numéro de niveau) */
  levels: BuildingLevelConfig[];
}

export const BUILDING_DEFINITIONS: Record<BuildingId, BuildingDefinition> = {

  dormitory: {
    id: 'dormitory',
    name: 'Dortoir',
    emoji: '🛏️',
    description: 'Héberge vos survivants.',
    minLevel: 1,
    levels: [
      { label: '—' }, // niveau 0 (inutilisé, minLevel=1)
      { label: '3 survivants max' },
      { label: '5 survivants max', cost: { wood: 15, iron: 10 } },
      { label: '8 survivants max', cost: { wood: 25, iron: 15, crystal: 3 } },
    ],
  },

  storage: {
    id: 'storage',
    name: 'Coffre',
    emoji: '📦',
    description: 'Stocke l\'équipement entre les runs.',
    minLevel: 1,
    levels: [
      { label: '—' },
      { label: '20 slots' },
      { label: '35 slots', cost: { wood: 10, iron: 10 } },
      { label: '50 slots', cost: { wood: 20, iron: 10, crystal: 2 } },
    ],
  },

  workshop: {
    id: 'workshop',
    name: 'Atelier',
    emoji: '🔨',
    description: 'Craftez équipement et consommables.',
    minLevel: 1,
    levels: [
      { label: '—' },
      { label: 'Recettes basiques' },
      { label: 'Recettes avancées', cost: { iron: 20, crystal: 5 } },
      { label: 'Recettes légendaires', cost: { iron: 15, crystal: 10, cursed_bone: 2 } },
    ],
  },

  watchtower: {
    id: 'watchtower',
    name: 'Vigie',
    emoji: '🗼',
    description: 'Débloque les expéditions idle.',
    minLevel: 0,
    levels: [
      { label: 'Non construite' },
      { label: 'Expéditions courtes', cost: { wood: 20, iron: 10 } },
      { label: 'Expéditions dangereuses', cost: { wood: 20, crystal: 5 } },
      { label: 'Expéditions abyssales', cost: { wood: 15, crystal: 10, cursed_bone: 3 } },
    ],
  },

  altar: {
    id: 'altar',
    name: 'Autel',
    emoji: '💀',
    description: 'Craft légendaire avec Os Maudit.',
    minLevel: 0,
    levels: [
      { label: 'Non construit' },
      { label: 'Rituels basiques', cost: { iron: 15, crystal: 5, cursed_bone: 1 } },
      { label: 'Rituels avancés', cost: { iron: 20, crystal: 10, cursed_bone: 3 } },
      { label: 'Craft Os Maudit', cost: { iron: 30, crystal: 15, cursed_bone: 5 } },
    ],
  },

  // Sprint 78 (Phase 7) — Taverne : mini-jeu Liar's Dice contre PNJ pirates
  tavern: {
    id: 'tavern',
    name: 'Taverne',
    emoji: '🍺',
    description: 'Jeu de dés des menteurs contre des pirates.',
    minLevel: 0,
    levels: [
      { label: 'Non construite' },
      { label: '1 adversaire (mises 5/10 or)', cost: { wood: 25, iron: 10 } },
      { label: '2 adversaires (mises 5/10/25 or)', cost: { wood: 20, iron: 15, crystal: 3 } },
      { label: '3 adversaires + lots premium', cost: { wood: 30, iron: 20, crystal: 5, cursed_bone: 2 } },
    ],
  },
};

/** Ordre d'affichage des bâtiments */
export const BUILDING_ORDER: BuildingId[] = ['dormitory', 'storage', 'workshop', 'watchtower', 'altar', 'tavern'];

// === LIMITES PAR NIVEAU ===

/** Capacité du coffre selon le niveau du bâtiment storage */
export const STORAGE_CAPACITY_BY_LEVEL: Record<number, number> = {
  1: 20,
  2: 35,
  3: 50,
};

/** Nombre max de survivants selon le niveau du dortoir */
export const SURVIVOR_SLOTS_BY_LEVEL: Record<number, number> = {
  1: 3,
  2: 5,
  3: 8,
};
